import { useCallback, useEffect, useRef, useState } from "react";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { useNavigate } from "react-router-dom";

import "../../styles/components/contactUs/contactEdgeIconTab.css";

const SIZE = 40;
const PEEK_LEFT = -10;
const DRAG_THRESHOLD = 6;
const ALLOW_X_DRAG = false;
const TOP_SAFE_MARGIN = 12;
const BOTTOM_SAFE = 120;
const STORAGE_KEY = "contactFabPos";

function clamp(val, minVal, maxVal) {
  return Math.min(Math.max(val, minVal), maxVal);
}

function getMinY() {
  if (typeof document === "undefined") return 120;
  const headerEl = document.querySelector(".headerContainer");
  const headerRect = headerEl?.getBoundingClientRect();
  const headerBottom = headerRect ? headerRect.bottom : 0;
  return Math.max(80, Math.round(headerBottom + TOP_SAFE_MARGIN));
}

function getYMax() {
  if (typeof window === "undefined") return 600;
  return window.innerHeight - SIZE - BOTTOM_SAFE;
}

function getDefaultPosition() {
  if (typeof window === "undefined") return { x: PEEK_LEFT, y: 400 };
  const minY = getMinY();
  const yMax = getYMax();
  const y = clamp(window.innerHeight * 0.72 - SIZE / 2, minY, yMax);
  return { x: PEEK_LEFT, y };
}

function getClampedPosition(px, py) {
  if (typeof window === "undefined") return { x: PEEK_LEFT, y: py };
  const xMin = -12;
  const xMax = window.innerWidth - SIZE + 12;
  const minY = getMinY();
  const yMax = getYMax();
  const x = ALLOW_X_DRAG ? clamp(px, xMin, xMax) : PEEK_LEFT;
  const y = clamp(py, minY, yMax);
  return { x, y };
}

export const FloatContactUsButtonComponent = ({ companyId }) => {
  const navigate = useNavigate();
  const [position, setPosition] = useState(getDefaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const positionRef = useRef(position);
  const buttonRef = useRef(null);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const p = JSON.parse(raw);
        if (typeof p.x === "number" && typeof p.y === "number") {
          const next = getClampedPosition(p.x, p.y);
          setPosition(next);
          positionRef.current = next;
        }
      } catch (_) {
        setPosition(getDefaultPosition());
      }
    } else {
      setPosition(getDefaultPosition());
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const pos = positionRef.current;
      const next = getClampedPosition(pos.x, pos.y);
      setPosition(next);
      positionRef.current = next;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const contactUsButtonClicked = useCallback(() => {
    navigate(`/company/${companyId}/contactUs`);
  }, [navigate, companyId]);

  const onPointerDown = useCallback(
    (e) => {
      const startX = e.clientX;
      const startY = e.clientY;
      let hasDragStarted = false;
      let captured = false;

      const handleMove = (e) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (!hasDragStarted && distance > DRAG_THRESHOLD) {
          hasDragStarted = true;
          if (buttonRef.current) {
            try {
              buttonRef.current.setPointerCapture(e.pointerId);
              captured = true;
            } catch (_) {}
          }
          document.body.style.userSelect = "none";
          setIsDragging(true);
        }

        if (hasDragStarted && typeof window !== "undefined") {
          const minY = getMinY();
          const yMax = getYMax();
          const newY = clamp(e.clientY - SIZE / 2, minY, yMax);
          const newX = ALLOW_X_DRAG
            ? clamp(e.clientX - SIZE / 2, -12, window.innerWidth - SIZE + 12)
            : PEEK_LEFT;
          const next = { x: newX, y: newY };
          setPosition(next);
          positionRef.current = next;
        }
      };

      const handleUp = (e) => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        if (captured && buttonRef.current) {
          try {
            buttonRef.current.releasePointerCapture(e.pointerId);
          } catch (_) {}
        }
        document.body.style.userSelect = "";
        setIsDragging(false);

        if (!hasDragStarted) {
          contactUsButtonClicked();
        }

        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positionRef.current));
        } catch (_) {}
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [contactUsButtonClicked]
  );

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`contactEdgeIconTab ${isDragging ? "isDragging" : ""}`}
      style={{ left: position.x, top: position.y }}
      onPointerDown={onPointerDown}
      aria-label="יצירת קשר"
    >
      <HeadsetMicOutlinedIcon aria-hidden />
    </button>
  );
};

export default FloatContactUsButtonComponent;
